/* eslint-disable consistent-return */
/**
 * @file MyStageView.jsx
 * @author Fernando Ruano
 * @description My Stage View
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Components
import AppCardLayout from '@nephro/components/AppCardLayout';

// Assets
import { ReactComponent as KidneyIcon } from '@nephro/assets/kidneyIcon.svg';
import { ReactComponent as KidneyIconE1 } from '@nephro/assets/kidneyCardE1.svg';
import { ReactComponent as KidneyIconE2 } from '@nephro/assets/kidneyCardE2.svg';
import { ReactComponent as KidneyIconE3a } from '@nephro/assets/kidneyCardE3a.svg';
import { ReactComponent as KidneyIconE3b } from '@nephro/assets/kidneyCardE3b.svg';
import { ReactComponent as KidneyIconE4 } from '@nephro/assets/kidneyCardE4.svg';
import { ReactComponent as KidneyIconE5 } from '@nephro/assets/kidneyCardE5.svg';

// Styles
import 'react-multi-carousel/lib/styles.css';

// Helpers
import { sortByDate } from '@nephro/commons/helpers/utils';
import { getPatientStage, getAlbumiCreati } from '../helpers/utils';
import { STAGE_INDICATOR } from '../helpers/constants';
import usePatientIndicators from '../helpers/usePatientIndicators';

const MyStageView = () => {
  // const [stageIndicator] = usePatientIndicators(STAGE_INDICATOR);
  const stageIndicator = usePatientIndicators(STAGE_INDICATOR);
  const { t: translate } = useTranslation();
  const [currentItem, setCurrentItem] = React.useState(0);
  const [albumiCreati, setAlbumiCreati] = React.useState(null);
  const [stageValue, setStageValue] = React.useState(null);

  // Stages Array Data
  const stagesArray = [
    {
      stage: '1',
      name: 'Estadío 1',
      color: 'border-stage-1',
      bgColor: 'bg-stage-1',
      kidneyIcon: <KidneyIconE1 width={164} />,
      unities: '> 90 ml/min/1.73 m2',
      description: 'Daño renal con tasa de filtración glomerular normal o alta',
      albuminuria: 'A1',
      albmDescription: 'RAC normal o aumento leve',
      albColor: 'bg-stage-2'
    },
    {
      stage: '2',
      name: 'Estadío 2',
      color: 'border-stage-2',
      bgColor: 'bg-stage-2',
      kidneyIcon: <KidneyIconE2 width={164} />,
      unities: '60-89 ml/min/1.73 m2',
      description: 'Daño renal leve',
      albuminuria: 'A1',
      albmDescription: 'RAC normal o aumento leve',
      albColor: 'bg-stage-2'
    },
    {
      stage: '3a',
      name: 'Estadío 3a',
      color: 'border-stage-3a',
      bgColor: 'bg-stage-3a',
      kidneyIcon: <KidneyIconE3a width={164} />,
      unities: '45-59 ml/min/1.73 m2',
      description: 'Daño renal leve a moderado',
      albuminuria: 'A2',
      albmDescription: 'Aumento moderado de RAC',
      albColor: 'bg-stage-3a'
    },
    {
      stage: '3b',
      name: 'Estadío 3b',
      color: 'border-stage-3b',
      bgColor: 'bg-stage-3b',
      kidneyIcon: <KidneyIconE3b width={164} />,
      unities: '30-44 ml/min/1.73 m2',
      description: 'Daño renal moderado a severo',
      albuminuria: 'A2',
      albmDescription: 'Aumento moderado de RAC',
      albColor: 'bg-stage-3a'
    },
    {
      stage: '4',
      name: 'Estadío 4',
      color: 'border-stage-4',
      bgColor: 'bg-stage-4',
      kidneyIcon: <KidneyIconE4 width={164} />,
      unities: '15-29 ml/min/1.73 m2',
      description: 'Daño renal severo',
      albuminuria: 'A3',
      albmDescription: 'Aumento severo de RAC',
      albColor: 'bg-stage-3b'
    },
    {
      stage: '5',
      name: 'Estadío 5',
      color: 'border-stage-5',
      bgColor: 'bg-stage-5',
      kidneyIcon: <KidneyIconE5 width={164} />,
      unities: '< 15 ml/min/1.73 m2',
      description: 'Enfermedad renal crónica avanzada',
      albuminuria: 'A3',
      albmDescription: 'Aumento severo de RAC',
      albColor: 'bg-stage-3b'
    }
  ];

  React.useEffect(() => {
    if (stageIndicator.length > 0) {
      const [stageItem] = stageIndicator?.filter(
        (itm) => itm.indicador_id === 1 && itm.parametro === 2
      );
      const albCrt = stageIndicator?.filter(
        (itm) => itm.indicador_id === 1 && itm.parametro !== 2
      );

      const patientStage = getPatientStage(stageItem.valor);
      const [recentDateItem] = sortByDate(albCrt, 'fecha');
      setStageValue(stageItem.valor);
      setAlbumiCreati(recentDateItem);

      const [itemStage] = stagesArray.filter(
        (itm) => patientStage === itm.stage
      );
      setCurrentItem(itemStage);
    }
  }, [stageIndicator]);

  return (
    <AppCardLayout>
      <>
        {stageIndicator !== undefined && stageIndicator.length > 0 && (
          <div className="h-full md:p-5 flex flex-col items-center overflow-y-auto">
            <div className="w-full pl-5 pt-5 mb-10 text-left flex flex-row items-center">
              <KidneyIcon width={40} />
              <h3 className="text-xl text-left mb-0 ml-2 text-baxterGray font-bold">
                {translate('dashboard.myStage')}
              </h3>
            </div>
            <div className="h-40 bg-red">{currentItem.kidneyIcon}</div>

            <div
              className={`rounded-3xl ${currentItem.bgColor} mt-10 pr-8 pl-8`}>
              <h3 className="mb-0 text-lg text-white font-bold">
                {currentItem.name}
              </h3>
            </div>
            <h3 className="text-base font-bold mt-5 mb-0 text-baxterGray">
              TFG {stageValue} mL/min/1.73m²
            </h3>
            <h3 className="text-base text-center text-baxterGray mt-2 pr-6 pl-6">
              {currentItem.description}
            </h3>
            {(currentItem.albuminuria || currentItem.albmDescription) &&
              albumiCreati && (
                <>
                  <div
                    className={`rounded-3xl ${currentItem.albColor} mt-8 pr-3 pl-3 pt-1 pb-1`}>
                    <h3 className="mb-0 text-base text-white font-bold">
                      Albuminuria{' '}
                      {albumiCreati && getAlbumiCreati(albumiCreati.valor).text}
                    </h3>
                  </div>
                  <h3 className="text-base font-bold mt-5 mb-0 text-baxterGray">
                    {albumiCreati.unidad === 'mg/gr' ? 'RAC' : 'Albuminuria'}{' '}
                    {albumiCreati.valor} {albumiCreati.unidad}
                  </h3>
                  <h3 className="text-base text-baxterGray mt-2">
                    {albumiCreati && getAlbumiCreati(albumiCreati.valor).desc}
                  </h3>
                </>
              )}
          </div>
        )}
      </>
    </AppCardLayout>
  );
};

/**
 * Component definition of proptypes.
 */
MyStageView.propTypes = {
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }).isRequired,
  loading: PropTypes.bool
};

export default MyStageView;
